import {first} from 'lodash';
import React, {FC, memo, useRef} from 'react';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Plural, Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontClickEvent} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {isTeamNamespace} from '../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {CellsContext} from '../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {TooltipShortcutFormatter} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipShortcutFormatter';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {ConversationStatusesEnum} from '../../../../../helpers/models/conversationHelpers';
import {ConversationBaseModel} from '../../../../../models/api/conversationModel';
import {getUserNamespace} from '../../../../../store/data/teammates/teammatesSelectors';
import {SnoozeDropdownContainer} from '../../../viewer/conversation/actions/snooze/snoozeDropdownContainer';
import {CellHoverArchive} from './hover/cellHoverArchive';
import {CellHoverIconBase} from './hover/cellHoverIconBase';
import {CellsHoverIconsBase} from './hover/cellHoverIconsBase';

/*
 * Constants.
 */

enum HoverActionsEnum {
  ARCHIVE = 'Archive',
  REOPEN = 'Reopen',
  SNOOZE = 'Snooze',
  UNSNOOZE = 'Unsnooze',
}

const archiveShortcut = <TooltipShortcutFormatter action={KeyboardActionsEnum.ARCHIVE} />;
const snoozeShortcut = <TooltipShortcutFormatter action={KeyboardActionsEnum.SNOOZE} />;

const tooltipContent = {
  [HoverActionsEnum.ARCHIVE]: {
    user: () => (
      <Trans id="cell-hover-icons-archive-in-my-inbox">Archive in my inbox {archiveShortcut}</Trans>
    ),
    delegated: () => (
      <Trans id="cell-hover-icons-archive-in-teammate-inbox">
        Archive in teammate inbox {archiveShortcut}
      </Trans>
    ),
    tag: () => <Trans id="cell-hover-icons-archive-in-my-tag">Archive in my tag {archiveShortcut}</Trans>,
    other: (inboxes: ReadonlyArray<InboxModel>) => {
      const inbox = first(inboxes);
      return (
        <Plural
          id="cell-hover-icons-archive-in-inboxes"
          value={inboxes.length}
          one={
            <>
              Archive in {inbox?.name} {archiveShortcut}
            </>
          }
          other={<>Archive in # inboxes {archiveShortcut}</>}
        />
      );
    },
  },
  [HoverActionsEnum.REOPEN]: {
    user: () => <Trans id="cell-hover-icons-reopen-in-my-inbox">Reopen in my inbox {archiveShortcut}</Trans>,
    delegated: () => (
      <Trans id="cell-hover-icons-reopen-in-teammate-inbox">Reopen in teammate inbox {archiveShortcut}</Trans>
    ),
    tag: () => <Trans id="cell-hover-icons-reopen-in-my-tag">Reopen in my tag {archiveShortcut}</Trans>,
    other: (inboxes: ReadonlyArray<InboxModel>) => {
      const inbox = first(inboxes);
      return (
        <Plural
          id="cell-hover-icons-reopen-in-inboxes"
          value={inboxes.length}
          one={
            <>
              Reopen in {inbox?.name} {archiveShortcut}
            </>
          }
          other={<>Reopen in # inboxes {archiveShortcut}</>}
        />
      );
    },
  },
  [HoverActionsEnum.SNOOZE]: {
    user: () => <Trans id="cell-hover-icons-snooze-in-my-inbox">Snooze in my inbox {snoozeShortcut}</Trans>,
    delegated: () => (
      <Trans id="cell-hover-icons-snooze-in-teammate-inbox">Snooze in teammate inbox {snoozeShortcut}</Trans>
    ),
    tag: () => <Trans id="cell-hover-icons-snooze-in-my-tag">Snooze in my tag {snoozeShortcut}</Trans>,
    other: (inboxes: ReadonlyArray<InboxModel>) => {
      const inbox = first(inboxes);
      return (
        <Plural
          id="cell-hover-icons-snooze-in-inboxes"
          value={inboxes.length}
          one={
            <>
              Snooze in {inbox?.name} {snoozeShortcut}
            </>
          }
          other={<>Snooze in # inboxes {snoozeShortcut}</>}
        />
      );
    },
  },
  [HoverActionsEnum.UNSNOOZE]: {
    user: () => (
      <Trans id="cell-hover-icons-unsnooze-in-my-inbox">Unsnooze in my inbox {snoozeShortcut}</Trans>
    ),
    delegated: () => (
      <Trans id="cell-hover-icons-unsnooze-in-teammate-inbox">
        Unsnooze in teammate inbox {snoozeShortcut}
      </Trans>
    ),
    tag: () => <Trans id="cell-hover-icons-unsnooze-in-my-tag">Unsnooze in my tag {snoozeShortcut}</Trans>,
    other: (inboxes: ReadonlyArray<InboxModel>) => {
      const inbox = first(inboxes);
      return (
        <Plural
          id="cell-hover-icons-unsnooze-in-inboxes"
          value={inboxes.length}
          one={
            <>
              Unsnooze in {inbox?.name} {snoozeShortcut}
            </>
          }
          other={<>Unsnooze in # inboxes {snoozeShortcut}</>}
        />
      );
    },
  },
};

/*
 * Props.
 */

interface CellHoverIconsProps {
  onTrashClick: (event: FrontClickEvent) => void;
  onOpenClick: (event: FrontClickEvent) => void;
  status: ConversationStatusesEnum;
  isSelected?: boolean;
  inboxes: ReadonlyArray<InboxModel>;
  hasReminder: boolean;
  isTeamContext: boolean;
  isDelegated: boolean;
  cellsContext: CellsContext;
  conversation: ConversationBaseModel;
  onSnoozeClick: (event: FrontClickEvent) => void;
  isSnoozeDropdownOpen: boolean;
  onSnoozeDropdownClose: () => void;
  shouldConsiderInPrivateTag: boolean;
  isCompact?: boolean;
}

/*
 * Components.
 */

interface RenderIconTooltipParams {
  inboxes: ReadonlyArray<InboxModel>;
  action: HoverActionsEnum;
  isTeamContext: boolean;
  isDelegated: boolean;
  shouldConsiderInPrivateTag: boolean;
}

function renderIconTooltip(params: RenderIconTooltipParams) {
  const {inboxes, action, isTeamContext, isDelegated, shouldConsiderInPrivateTag} = params;

  if (isDelegated) {
    return tooltipContent[action].delegated();
  }

  if (shouldConsiderInPrivateTag) {
    return tooltipContent[action].tag();
  }

  return isTeamContext ? tooltipContent[action].other(inboxes) : tooltipContent[action].user();
}

export const CellHoverIcons: FC<CellHoverIconsProps> = memo((props) => {
  const theme = useUserTheme();
  const {
    isTeamContext,
    isDelegated,
    conversation,
    isSnoozeDropdownOpen,
    onSnoozeClick,
    hasReminder,
    shouldConsiderInPrivateTag,
  } = props;

  const userNamespace = useAppSelector(getUserNamespace);
  const accessibleConversationInboxes = props.inboxes.filter(
    (inbox) => inbox.namespace === userNamespace || isTeamNamespace(inbox.namespace),
  );

  const isArchived = props.status === ConversationStatusesEnum.ARCHIVED && !hasReminder;
  const isSnoozed = props.status === ConversationStatusesEnum.ARCHIVED && hasReminder;
  const isTrashed = props.status === ConversationStatusesEnum.TRASHED;

  const snoozeStandardColor = isSnoozed ? theme.palette.orange.shade40 : theme.greys.shade80;

  const archiveLabel = renderIconTooltip({
    inboxes: accessibleConversationInboxes,
    action: isArchived ? HoverActionsEnum.REOPEN : HoverActionsEnum.ARCHIVE,
    isTeamContext,
    isDelegated,
    shouldConsiderInPrivateTag,
  });

  const snoozeLabel = renderIconTooltip({
    inboxes: accessibleConversationInboxes,
    action: isSnoozed ? HoverActionsEnum.UNSNOOZE : HoverActionsEnum.SNOOZE,
    isTeamContext,
    isDelegated,
    shouldConsiderInPrivateTag,
  });

  // Don't trigger a double click on the cell when we double click on a hover icon.
  const onDoubleClick = (event: FrontClickEvent) => {
    event.preventDefault();
  };

  const snoozeButtonRef = useRef<HTMLDivElement>(null);

  return (
    <CellsHoverIconsBase isCompact={props.isCompact}>
      {!props.hasReminder && !isTrashed && (
        <CellHoverIconBase interactionId="p2-quick-actions-archive">
          <CellHoverArchive
            cellsContext={props.cellsContext}
            conversations={[props.conversation]}
            onDoubleClick={onDoubleClick}
            archiveLabel={archiveLabel}
            placement={(props.isCompact && 'bottom') || undefined}
          />
        </CellHoverIconBase>
      )}
      <CellHoverIconBase>
        <IconButton
          ref={snoozeButtonRef}
          iconName={isSnoozed ? 'snoozeFilledSmall' : 'snoozeSmall'}
          onClick={isSnoozed ? props.onOpenClick : onSnoozeClick}
          onDoubleClick={onDoubleClick}
          label={snoozeLabel}
          labelMargin={2}
          color={snoozeStandardColor}
          hoverColor={snoozeStandardColor}
          backgroundHoverColor={theme.alphas.gray10}
          backgroundColor={isSnoozeDropdownOpen ? theme.alphas.gray10 : undefined}
          placement={isSnoozed ? 'top' : 'bottom'}
          size={VisualSizesEnum.SMALL}
        />
      </CellHoverIconBase>
      {isSnoozeDropdownOpen && (
        <SnoozeDropdownContainer
          anchor={snoozeButtonRef}
          onRequestClose={props.onSnoozeDropdownClose}
          cellsContext={props.cellsContext}
          conversations={[conversation]}
          placement={props.isCompact ? 'bottom-start' : 'right-start'}
          isBulkActionRequired={false}
          shouldHideDropdownHeader
          isSnoozeFromCellHoverIcon
        />
      )}
    </CellsHoverIconsBase>
  );
});
